import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  fetchNotificationEmails,
  fetchReservationSetting,
  fetchReservationStoreImages,
  fetchReservationStoreInfo,
  getReservationMailPreview,
  saveNotificationEmails,
  saveReservationStoreImages,
  saveReservationStoreInfo,
} from '../../../Redux/ReservationSettings/reservationSettingsApi';
import {
  MAIL_PREVIEW_TYPE_LABEL,
  PrivacyPolicyType,
  ReservationMailPreview,
  ReservationMailPreviewType,
} from '../../../types/reservation';
import ReservationImageUploader from './ReservationImageUploader';
import {
  extractApiMessage,
  SavedSnackbar,
  TabDescription,
  UnsavedChangesBar,
  useDirtyNotifier,
  useSaveFeedback,
} from './SettingsShared';

interface Props {
  storeId: string;
  /** dirty（サーバ値と差分あり）の変化を親へ通知する（タブ切替時の確認用） */
  onDirtyChange?: (dirty: boolean) => void;
}

const MAX_EMAILS = 3;
/** 店舗写真ギャラリーの最大枚数（契約C） */
const MAX_GALLERY_IMAGES = 6;
// 簡易メール形式チェック（最終強制は BE）
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** メールプレビューダイアログの種類タブの並び */
const PREVIEW_TYPES: ReservationMailPreviewType[] = [
  'RECEIVED',
  'CONFIRMED',
  'CANCELLED',
  'REMINDER',
];

/** 店舗案内の詳細項目（契約D・migration 020 追加分）のフォームキー */
type InfoFieldKey =
  | 'phone'
  | 'accessInfo'
  | 'businessHoursNote'
  | 'closedDaysNote'
  | 'paymentMethodsNote'
  | 'serviceChargeNote'
  | 'cancellationPolicyNote'
  | 'dressCodeNote'
  | 'seatsNote'
  | 'smokingNote'
  | 'childrenNote'
  | 'petNote'
  | 'parkingNote'
  | 'storeNotes';

/** 契約Dの14項目（見出し=ラベル・表示順は契約の並び） */
const INFO_FIELDS: {
  key: InfoFieldKey;
  label: string;
  placeholder?: string;
  multiline?: boolean;
}[] = [
  { key: 'phone', label: '電話番号', placeholder: '例: 03-1234-5678' },
  { key: 'accessInfo', label: 'アクセス', placeholder: '例: JR各線 秋葉原駅 徒歩2分', multiline: true },
  { key: 'businessHoursNote', label: '営業時間', placeholder: '例: 17:30〜23:00（22:00）※（）内はLO', multiline: true },
  { key: 'closedDaysNote', label: '定休日', placeholder: '例: 土曜日 日曜日 祝日' },
  { key: 'paymentMethodsNote', label: 'お支払い', placeholder: '例: カード可（VISA・Master）、QRコード決済可', multiline: true },
  { key: 'serviceChargeNote', label: 'サービス料・チャージ', placeholder: '例: サービス料10%' },
  { key: 'cancellationPolicyNote', label: 'キャンセル料', placeholder: '例: 前日キャンセルはコース料金の50%を頂戴します', multiline: true },
  { key: 'dressCodeNote', label: 'ドレスコード・服装', placeholder: '例: スマートカジュアル' },
  { key: 'seatsNote', label: '座席', placeholder: '例: 13席（個室3室、カウンター3卓）' },
  { key: 'smokingNote', label: '喫煙可否', placeholder: '例: 全席禁煙' },
  { key: 'childrenNote', label: 'お子様の同伴', placeholder: '例: 小学生以上のお子様のみご案内できます' },
  { key: 'petNote', label: 'ペットの同伴', placeholder: '例: ペット同伴不可' },
  { key: 'parkingNote', label: '駐車場', placeholder: '例: 近隣にコインパーキングあり' },
  { key: 'storeNotes', label: '店舗注意事項', placeholder: '例: 20時以降のご入店はお電話にてご確認ください', multiline: true },
];

const emptyDetails = (): Record<InfoFieldKey, string> =>
  INFO_FIELDS.reduce(
    (acc, field) => ({ ...acc, [field.key]: '' }),
    {} as Record<InfoFieldKey, string>,
  );

/**
 * タブ⑧店舗情報（旧 通知・店舗情報）。
 * - 個人情報の取り扱い（必須）: URL またはテキスト。未設定の間は公開予約ページで受付できない（フィードバック3）
 * - 通知メール: 予約の受付・キャンセル通知の宛先（0〜3件）
 * - 店舗名・住所: 店舗マスタからの読み取り専用表示（変更は店舗設定へ誘導）
 * - 店舗情報: 予約ページ・お客様向けメールに表示する案内（契約Dの14項目 + 連絡先/案内文）
 * - 店舗写真: 予約ページのヒーロー1枚 + ギャラリー最大6枚（フィードバック2）
 * 未保存の変更がある間は画面下部に sticky 保存バーを表示する（全セクション横断・フィードバック3）。
 */
const NotificationTab: React.FC<Props> = ({ storeId, onDirtyChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const notificationEmails = useSelector(
    (state: RootState) => state.reservationSettings.notificationEmails,
  );
  const storeInfo = useSelector((state: RootState) => state.reservationSettings.storeInfo);
  const storeImages = useSelector((state: RootState) => state.reservationSettings.storeImages);
  // 承認モード（B3・メールプレビューの送信フロー説明の出し分けに使う）
  const setting = useSelector((state: RootState) => state.reservationSettings.setting);

  const [loaded, setLoaded] = useState(false);
  // 通知メールフォーム
  const [emails, setEmails] = useState<string[]>([]);
  const [savingEmails, setSavingEmails] = useState(false);
  // 店舗情報フォーム
  const [contactInfo, setContactInfo] = useState('');
  const [note, setNote] = useState('');
  const [details, setDetails] = useState<Record<InfoFieldKey, string>>(emptyDetails());
  const [savingInfo, setSavingInfo] = useState(false);
  // 個人情報の取り扱いフォーム（フィードバック3。'' = 未設定）
  const [ppType, setPpType] = useState<PrivacyPolicyType | ''>('');
  const [ppUrl, setPpUrl] = useState('');
  const [ppText, setPpText] = useState('');
  // 店舗写真フォーム（ヒーローは最大1枚として配列で扱う）
  const [heroUrls, setHeroUrls] = useState<string[]>([]);
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [savingImages, setSavingImages] = useState(false);
  // メールプレビューダイアログ
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState<ReservationMailPreviewType>('RECEIVED');
  const [preview, setPreview] = useState<ReservationMailPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const { savedOpen, closeSaved, showSaved, error, setError, showError } = useSaveFeedback();

  // ダイアログ表示中に種類を切り替えるたび実テンプレを取得する
  useEffect(() => {
    if (!previewOpen) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    getReservationMailPreview(storeId, previewType)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(extractApiMessage(e));
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [previewOpen, previewType, storeId]);

  useEffect(() => {
    Promise.allSettled([
      dispatch(fetchNotificationEmails(storeId)).unwrap(),
      dispatch(fetchReservationStoreInfo(storeId)).unwrap(),
      dispatch(fetchReservationStoreImages(storeId)).unwrap(),
      // 承認モード（B3）取得。単独タブ表示だと受付設定タブ未経由のため setting が無いことがある
      dispatch(fetchReservationSetting(storeId)).unwrap(),
    ]).then((results) => {
      const failed = results.find((r) => r.status === 'rejected');
      if (failed && failed.status === 'rejected') showError(failed.reason);
      setLoaded(true);
    });
  }, [dispatch, storeId, showError]);

  useEffect(() => {
    setEmails(notificationEmails);
  }, [notificationEmails]);

  // サーバ値→店舗情報フォームの同期（破棄ボタンでも使う）
  const resetInfoForm = () => {
    // null（未設定）なら空フォームのまま
    setContactInfo(storeInfo?.contactInfo ?? '');
    setNote(storeInfo?.note ?? '');
    setDetails(
      INFO_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field.key]: storeInfo?.[field.key] ?? '' }),
        {} as Record<InfoFieldKey, string>,
      ),
    );
    setPpType(storeInfo?.privacyPolicyType ?? '');
    setPpUrl(storeInfo?.privacyPolicyUrl ?? '');
    setPpText(storeInfo?.privacyPolicyText ?? '');
  };

  useEffect(() => {
    resetInfoForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeInfo]);

  useEffect(() => {
    setHeroUrls(storeImages?.heroImageUrl ? [storeImages.heroImageUrl] : []);
    setGalleryUrls(storeImages?.galleryImageUrls ?? []);
  }, [storeImages]);

  // ---- dirty 判定（セクション別 → 全体。sticky 保存バーとタブ切替確認に使う） ----
  const emailsDirty =
    loaded && JSON.stringify(emails) !== JSON.stringify(notificationEmails);
  const infoDirty =
    loaded &&
    (contactInfo !== (storeInfo?.contactInfo ?? '') ||
      note !== (storeInfo?.note ?? '') ||
      INFO_FIELDS.some((field) => details[field.key] !== (storeInfo?.[field.key] ?? '')) ||
      ppType !== (storeInfo?.privacyPolicyType ?? '') ||
      ppUrl !== (storeInfo?.privacyPolicyUrl ?? '') ||
      ppText !== (storeInfo?.privacyPolicyText ?? ''));
  const imagesDirty =
    loaded &&
    JSON.stringify({ hero: heroUrls[0] ?? null, gallery: galleryUrls }) !==
      JSON.stringify({
        hero: storeImages?.heroImageUrl ?? null,
        gallery: storeImages?.galleryImageUrls ?? [],
      });
  const dirty = emailsDirty || infoDirty || imagesDirty;
  useDirtyNotifier(dirty, onDirtyChange);

  // 個人情報の取り扱いが有効に設定済みか（未設定の間は公開受付不可の警告を出す）
  const privacyConfigured =
    (ppType === 'URL' && ppUrl.trim() !== '') || (ppType === 'TEXT' && ppText.trim() !== '');

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const saveEmails = (silent = false): Promise<void> => {
    const trimmed = emails.map((e) => e.trim()).filter((e) => e !== '');
    if (trimmed.some((e) => !EMAIL_RE.test(e))) {
      setError('メールアドレスの形式が正しくありません。');
      return Promise.reject(new Error('validation'));
    }
    setSavingEmails(true);
    return dispatch(saveNotificationEmails({ storeId, emails: trimmed }))
      .unwrap()
      .then(() => {
        if (!silent) showSaved();
      })
      .catch((e: unknown) => {
        showError(e);
        throw e;
      })
      .finally(() => setSavingEmails(false));
  };

  const saveInfo = (silent = false): Promise<void> => {
    if (!contactInfo.trim()) {
      setError('連絡先を入力してください。');
      return Promise.reject(new Error('validation'));
    }
    // 個人情報の取り扱い: 方式を選んだ場合は対応する値が必須（BE の PUT バリデーションと同一）
    if (ppType === 'URL') {
      const url = ppUrl.trim();
      if (!url) {
        setError('個人情報の取り扱いの掲載先URLを入力してください。');
        return Promise.reject(new Error('validation'));
      }
      if (!/^https?:\/\//.test(url)) {
        setError('個人情報の取り扱いの掲載先URLは http:// または https:// で始まる形式で入力してください。');
        return Promise.reject(new Error('validation'));
      }
    }
    if (ppType === 'TEXT' && !ppText.trim()) {
      setError('個人情報の取り扱いの本文を入力してください。');
      return Promise.reject(new Error('validation'));
    }
    const toNullable = (value: string): string | null => (value.trim() ? value.trim() : null);
    const detailPayload = INFO_FIELDS.reduce(
      (acc, field) => ({ ...acc, [field.key]: toNullable(details[field.key]) }),
      {} as Record<InfoFieldKey, string | null>,
    );
    setSavingInfo(true);
    return dispatch(
      saveReservationStoreInfo({
        storeId,
        info: {
          contactInfo: contactInfo.trim(),
          note: toNullable(note),
          ...detailPayload,
          privacyPolicyType: ppType === '' ? null : ppType,
          privacyPolicyUrl: ppType === 'URL' ? ppUrl.trim() : null,
          privacyPolicyText: ppType === 'TEXT' ? ppText.trim() : null,
        },
      }),
    )
      .unwrap()
      .then(() => {
        if (!silent) showSaved();
      })
      .catch((e: unknown) => {
        showError(e);
        throw e;
      })
      .finally(() => setSavingInfo(false));
  };

  const saveImages = (silent = false): Promise<void> => {
    setSavingImages(true);
    return dispatch(
      saveReservationStoreImages({
        storeId,
        images: { heroImageUrl: heroUrls[0] ?? null, galleryImageUrls: galleryUrls },
      }),
    )
      .unwrap()
      .then(() => {
        if (!silent) showSaved();
      })
      .catch((e: unknown) => {
        showError(e);
        throw e;
      })
      .finally(() => setSavingImages(false));
  };

  /** sticky 保存バーの「保存」: 差分のあるセクションをまとめて保存する */
  const saveAllDirty = () => {
    const jobs: Promise<void>[] = [];
    if (emailsDirty) jobs.push(saveEmails(true));
    if (infoDirty) jobs.push(saveInfo(true));
    if (imagesDirty) jobs.push(saveImages(true));
    Promise.all(jobs)
      .then(() => showSaved())
      .catch(() => {
        /* エラーは各セクションで表示済み */
      });
  };

  /** sticky 保存バーの「破棄」: 全セクションをサーバ値へ戻す */
  const discardAll = () => {
    setEmails(notificationEmails);
    resetInfoForm();
    setHeroUrls(storeImages?.heroImageUrl ? [storeImages.heroImageUrl] : []);
    setGalleryUrls(storeImages?.galleryImageUrls ?? []);
    setError(null);
  };

  return (
    <Box>
      <TabDescription text="予約の通知先と、お客様向けの予約ページ・メールに表示する店舗情報を設定します" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 個人情報の取り扱い（必須・フィードバック3）。未設定の間は公開予約ページで受付できない */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          個人情報の取り扱い（必須）
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          予約時にお客様へ同意いただく、貴店の個人情報の取り扱いです。既存のポリシーページのURLを指定するか、本文を入力してください（本文は予約ページから貴店名義で閲覧できます）
        </Typography>
        {!privacyConfigured && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            個人情報の取り扱いが未設定です。設定するまで予約ページで受付できません。
          </Alert>
        )}
        <RadioGroup
          value={ppType}
          onChange={(e) => setPpType(e.target.value as PrivacyPolicyType)}
        >
          <FormControlLabel
            value="URL"
            control={<Radio size="small" />}
            label="URLで指定する（自社サイトのプライバシーポリシーなど）"
            disabled={savingInfo}
          />
          {ppType === 'URL' && (
            <TextField
              label="掲載先URL"
              value={ppUrl}
              onChange={(e) => setPpUrl(e.target.value)}
              placeholder="例: https://example.com/privacy"
              fullWidth
              margin="dense"
              size="small"
              disabled={savingInfo}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          )}
          <FormControlLabel
            value="TEXT"
            control={<Radio size="small" />}
            label="テキストで入力する（FEASTの予約ページに貴店名義で掲載）"
            disabled={savingInfo}
          />
          {ppType === 'TEXT' && (
            <TextField
              label="個人情報の取り扱い（本文）"
              value={ppText}
              onChange={(e) => setPpText(e.target.value)}
              placeholder={'例: 当店は、ご予約の受付・確認のご連絡のためにお客様の氏名・連絡先を利用します。\n法令に基づく場合を除き、第三者に提供することはありません。'}
              multiline
              minRows={5}
              fullWidth
              margin="dense"
              size="small"
              disabled={savingInfo}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </RadioGroup>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => saveInfo().catch(() => {})}
            disabled={savingInfo}
          >
            {savingInfo ? <CircularProgress size={22} /> : '保存'}
          </Button>
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          通知メール
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          予約の受付・キャンセルをメールで受け取る（最大3件）
        </Typography>
        {emails.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
            通知先は未設定です。
          </Typography>
        )}
        {emails.map((email, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <TextField
              type="email"
              value={email}
              onChange={(e) =>
                setEmails(emails.map((v, i) => (i === index ? e.target.value : v)))
              }
              size="small"
              fullWidth
              placeholder="mail@example.com"
              disabled={savingEmails}
            />
            <IconButton
              size="small"
              onClick={() => setEmails(emails.filter((_, i) => i !== index))}
              disabled={savingEmails}
              aria-label="通知先を削除"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setEmails([...emails, ''])}
            disabled={emails.length >= MAX_EMAILS || savingEmails}
          >
            通知先を追加
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => saveEmails().catch(() => {})}
            disabled={savingEmails}
          >
            {savingEmails ? <CircularProgress size={22} /> : '保存'}
          </Button>
        </Box>
      </Paper>

      {/* 店舗名・住所は店舗マスタから引き継ぎ（読み取り専用・契約D） */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          店舗名・住所
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          店舗設定の内容が予約ページ・お客様向けメールにそのまま表示されます（ここでは変更できません）
        </Typography>
        <TextField
          label="店舗名"
          value={storeInfo?.storeName ?? ''}
          fullWidth
          margin="dense"
          size="small"
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="住所"
          value={storeInfo?.address ?? ''}
          fullWidth
          margin="dense"
          size="small"
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            size="small"
            endIcon={<OpenInNewIcon />}
            onClick={() => navigate(`/${storeId}/manage/store`)}
          >
            店舗設定で変更
          </Button>
        </Box>
      </Paper>

      {/* 店舗写真（ヒーロー+ギャラリー・契約C） */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          店舗写真
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          メイン画像は予約ページの最上部に、ギャラリーは店舗情報セクションに表示されます
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          メイン画像（1枚）
        </Typography>
        <ReservationImageUploader
          storeId={storeId}
          kind="STORE"
          urls={heroUrls}
          onChange={setHeroUrls}
          maxCount={1}
          addLabel="メイン画像を追加"
          disabled={savingImages}
          onError={showError}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>
          ギャラリー（最大{MAX_GALLERY_IMAGES}枚）
        </Typography>
        <ReservationImageUploader
          storeId={storeId}
          kind="STORE"
          urls={galleryUrls}
          onChange={setGalleryUrls}
          maxCount={MAX_GALLERY_IMAGES}
          addLabel="写真を追加"
          disabled={savingImages}
          onError={showError}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => saveImages().catch(() => {})}
            disabled={savingImages}
          >
            {savingImages ? <CircularProgress size={22} /> : '保存'}
          </Button>
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          店舗情報
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          予約ページの店舗情報セクションと、お客様に届く予約メールに表示されます（未入力の項目は表示されません）
        </Typography>
        {/* 必須の連絡先・補足を先頭に配置する（B2: 必須項目を最初に入力させる） */}
        <Typography variant="subtitle2" gutterBottom>
          連絡先（必須）・補足
        </Typography>
        {!contactInfo.trim() && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            連絡先が未入力です。未入力だと予約ページで受付できません。
          </Alert>
        )}
        <TextField
          label="連絡先（必須）"
          required
          error={!contactInfo.trim()}
          value={contactInfo}
          onChange={(e) => setContactInfo(e.target.value)}
          placeholder="例: ご連絡は info@example.com へお願いします"
          helperText="お客様からお店への連絡手段（メールアドレス・SNS・電話番号など）。未入力だと予約ページで受付できません"
          fullWidth
          margin="dense"
          size="small"
          disabled={savingInfo}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="補足（任意）"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={'例: 営業時間外のご連絡はメールへお願いします。\n当日のご変更は承れない場合があります。'}
          multiline
          minRows={3}
          fullWidth
          margin="dense"
          size="small"
          disabled={savingInfo}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
          店舗案内（任意）
        </Typography>
        {INFO_FIELDS.map((field) => (
          <TextField
            key={field.key}
            label={field.label}
            value={details[field.key]}
            onChange={(e) => setDetails({ ...details, [field.key]: e.target.value })}
            placeholder={field.placeholder}
            multiline={field.multiline}
            minRows={field.multiline ? 2 : undefined}
            fullWidth
            margin="dense"
            size="small"
            disabled={savingInfo}
            InputLabelProps={{ shrink: true }}
          />
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Button
            size="small"
            startIcon={<MailOutlineIcon />}
            onClick={() => setPreviewOpen(true)}
          >
            お客様に届くメールをプレビュー
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => saveInfo().catch(() => {})}
            disabled={savingInfo}
          >
            {savingInfo ? <CircularProgress size={22} /> : '保存'}
          </Button>
        </Box>
      </Paper>

      {/* メール文プレビュー（現在の設定・案内文で実テンプレを描画した内容を表示する） */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>メールプレビュー</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            サンプルの予約データと現在の設定で、お客様に届くメールの内容を表示しています。
          </Typography>
          {/* 承認モード別の送信フロー説明（B3）。AUTO=確定のみ自動送信 / MANUAL=受付→確定 */}
          {setting?.approvalMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {setting.approvalMode === 'AUTO'
                ? '現在の設定（自動で確定する）では、予約成立時に「確定」メールが自動送信されます（「受付」メールは送信されません）。'
                : '現在の設定（店舗が確認して確定する）では、申込時に「受付」メール、店舗が承認したときに「確定」メールが送信されます（受付→確定の2段階）。'}
            </Alert>
          )}
          <Tabs
            value={previewType}
            onChange={(_, value: ReservationMailPreviewType) => setPreviewType(value)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {PREVIEW_TYPES.map((t) => (
              <Tab key={t} value={t} label={MAIL_PREVIEW_TYPE_LABEL[t]} />
            ))}
          </Tabs>
          {/* 選択中の種別が現在の承認モードで送信されるかの位置づけ補足（B3） */}
          {setting?.approvalMode === 'AUTO' && previewType === 'RECEIVED' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              自動で確定する設定のため、この「受付」メールは送信されません（確定メールのみ送信されます）。
            </Alert>
          )}
          {setting?.approvalMode === 'MANUAL' && previewType === 'RECEIVED' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              このメールは申込を受け付けた時点で送信されます（確定は店舗の承認後）。
            </Typography>
          )}
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : previewError ? (
            <Alert severity="error">{previewError}</Alert>
          ) : preview ? (
            <>
              <Typography variant="subtitle2" gutterBottom>
                件名: {preview.subject}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {preview.body}
                </Typography>
              </Paper>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setPreviewOpen(false)}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 未保存の変更がある間だけ表示される sticky 保存バー（差分のあるセクションをまとめて保存） */}
      <UnsavedChangesBar
        dirty={dirty}
        saving={savingEmails || savingInfo || savingImages}
        onSave={saveAllDirty}
        onDiscard={discardAll}
      />

      <SavedSnackbar open={savedOpen} onClose={closeSaved} />
    </Box>
  );
};

export default NotificationTab;
